import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Controls,
  MiniMap,
  Background,
  Panel,
  useReactFlow,
  useNodesState,
  useEdgesState,
  MarkerType,
  getRectOfNodes,
  getTransformForBounds,
  getViewportForBounds
} from 'reactflow';
import { toPng, toSvg } from 'html-to-image';
import 'reactflow/dist/style.css';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Toolbar,
  AppBar,
  Tooltip,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  Stack,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Save as SaveIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  History as HistoryIcon,
  Comment as CommentIcon,
  Settings as SettingsIcon,
  Image as ImageIcon,
  Code as CodeIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  AutoSave as AutoSaveIcon,
  CloudUpload as PublishIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useProcessFlowStore from '../../stores/processFlowStore';
import StartNode from './nodes/StartNode';
import EndNode from './nodes/EndNode';
import APINode from './nodes/APINode';
import DatabaseNode from './nodes/DatabaseNode';
import EmailNode from './nodes/EmailNode';
import ApprovalNode from './nodes/ApprovalNode';
import DecisionNode from './nodes/DecisionNode';
import ParallelGatewayNode from './nodes/ParallelGatewayNode';
import MergeGatewayNode from './nodes/MergeGatewayNode';
import LoopNode from './nodes/LoopNode';
import TimerNode from './nodes/TimerNode';
import ErrorHandlerNode from './nodes/ErrorHandlerNode';
import SubprocessNode from './nodes/SubprocessNode';
import NoteNode from './nodes/NoteNode';
import NodeEditor from './NodeEditor';
import ShareDialog from './ShareDialog';
import CommentPanel from './CommentPanel';
import VersionHistory from './VersionHistory';
import FlowSettings from './FlowSettings';
import NodePalette from './NodePalette';
import { useSnackbar } from 'notistack';

// Define node types
const nodeTypes = {
  start: StartNode,
  end: EndNode,
  api: APINode,
  database: DatabaseNode,
  email: EmailNode,
  approval: ApprovalNode,
  decision: DecisionNode,
  parallelGateway: ParallelGatewayNode,
  mergeGateway: MergeGatewayNode,
  loop: LoopNode,
  timer: TimerNode,
  errorHandler: ErrorHandlerNode,
  subprocess: SubprocessNode,
  note: NoteNode
};

// Define edge types
const edgeTypes = {
  default: 'default',
  straight: 'straight',
  step: 'step',
  smoothstep: 'smoothstep',
  bezier: 'bezier'
};


function ProcessFlowEditor({ readOnly = false }) {
  const { id: flowIdFromParams } = useParams();
  const flowId = flowIdFromParams;
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNodeEditor, setShowNodeEditor] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [nodeMenuAnchor, setNodeMenuAnchor] = useState(null);
  const [contextMenuNode, setContextMenuNode] = useState(null);
  const [draggedNodeType, setDraggedNodeType] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const {
    currentFlow,
    fetchProcessFlow,
    updateCurrentFlow,
    updateProcessFlow,
    createProcessFlow,
    initializeEmptyFlow,
    clearCurrentFlow,
    autoSave,
    isLoading,
    isAutoSaving,
    hasUnsavedChanges,
    lastAutoSaveAt
  } = useProcessFlowStore();

  const { project, setViewport, getViewport } = useReactFlow();

  // Helper function to get theme-based colors for node types
  const getNodeThemeColor = useCallback((nodeType) => {
    switch (nodeType) {
      case 'start': return theme.palette.success.main;
      case 'end': return theme.palette.error.main;
      case 'decision': return theme.palette.warning.main;
      case 'parallelGateway':
      case 'mergeGateway': return theme.palette.secondary.main;
      case 'loop': return theme.palette.primary.main;
      case 'api': return theme.palette.info.main;
      case 'database': return theme.palette.secondary.main;
      case 'email': return theme.palette.error.light;
      case 'approval': return theme.palette.primary.dark;
      case 'timer': return theme.palette.warning.dark;
      case 'errorHandler': return theme.palette.error.main;
      case 'subprocess': return theme.palette.info.dark;
      case 'note': return theme.palette.warning.light;
      default: return theme.palette.info.main;
    }
  }, [theme]);

  // ReactFlow styles that adapt to theme mode
  const reactFlowStyles = useMemo(() => ({
    background: theme.palette.background.default,
    '.react-flow__edge-path': {
      stroke: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
    },
    '.react-flow__edge.selected .react-flow__edge-path': {
      stroke: theme.palette.primary.main,
      strokeWidth: 2,
    },
    '.react-flow__handle': {
      background: theme.palette.primary.main,
      border: `2px solid ${theme.palette.background.paper}`,
    },
    '.react-flow__handle-connecting': {
      background: theme.palette.secondary.main,
    },
    '.react-flow__attribution': {
      background: theme.palette.background.paper,
      color: theme.palette.text.secondary,
    },
    '.react-flow__background': {
      backgroundColor: theme.palette.background.default,
    },
    '.react-flow__minimap': {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      '& .react-flow__minimap-mask': {
        fill: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      },
      '& .react-flow__minimap-node': {
        fill: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
        stroke: 'none',
      },
    },
    '.react-flow__controls': {
      background: theme.palette.background.paper,
      borderColor: theme.palette.divider,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[2],
      '& button': {
        background: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        color: theme.palette.text.primary,
        '&:hover': {
          background: theme.palette.action.hover,
        },
        '&.react-flow__controls-button': {
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:last-child': {
            borderBottom: 'none',
          },
        },
      },
    },
  }), [theme]);

  // Load flow data
  useEffect(() => {
    if (flowId) {
      fetchProcessFlow(flowId);
    } else {
      // Initialize empty flow for create mode
      initializeEmptyFlow();
    }

    // Cleanup on unmount
    return () => {
      if (!flowId) {
        clearCurrentFlow();
      }
    };
  }, [flowId, fetchProcessFlow, initializeEmptyFlow, clearCurrentFlow]);

  // Sync with store
  useEffect(() => {
    if (currentFlow) {
      setNodes(currentFlow.nodes || []);
      setEdges(currentFlow.edges || []);
      if (currentFlow.viewport) {
        setViewport(currentFlow.viewport);
      }
    }
  }, [currentFlow, setNodes, setEdges, setViewport]);

  // Custom event listeners for node interactions
  useEffect(() => {
    const handleEditNode = (event) => {
      const { nodeId } = event.detail;
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
        setSelectedNode(node);
        setShowNodeEditor(true);
      }
    };

    const handleNodeContextMenu = (event) => {
      const { nodeId, x, y } = event.detail;
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
        setSelectedNode(node);
        setContextMenuNode(node);
        setNodeMenuAnchor({ mouseX: x, mouseY: y });
      }
    };

    window.addEventListener('editNode', handleEditNode);
    window.addEventListener('nodeContextMenu', handleNodeContextMenu);

    return () => {
      window.removeEventListener('editNode', handleEditNode);
      window.removeEventListener('nodeContextMenu', handleNodeContextMenu);
    };
  }, [nodes]);

  // Handle node connection
  const onConnect = useCallback((params) => {
    if (readOnly) return;

    const newEdge = {
      ...params,
      id: `e${Date.now()}`,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20
      },
      style: {
        strokeWidth: 2,
        stroke: theme.palette.primary.main
      }
    };

    setEdges((eds) => addEdge(newEdge, eds));
    updateCurrentFlow({ edges: [...edges, newEdge] });
  }, [readOnly, setEdges, edges, updateCurrentFlow, theme]);

  // Debounced update function
  const debouncedUpdate = useMemo(() => {
    let timeoutId;
    return (updateFn) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateFn, 300); // 300ms debounce
    };
  }, []);

  // Handle node changes
  const handleNodesChange = useCallback((changes) => {
    if (readOnly) return;
    onNodesChange(changes);

    // Don't update the store during dragging to prevent performance issues
    // The store will be updated when dragging stops (onNodeDragStop)
    const hasDragChanges = changes.some(change => change.type === 'position' && change.dragging);

    if (!hasDragChanges) {
      // Only update store for non-drag changes (selection, deletion, etc.)
      setNodes((currentNodes) => {
        const updatedNodes = applyNodeChanges(changes, currentNodes);
        updateCurrentFlow({ nodes: updatedNodes });
        return updatedNodes;
      });
    }
  }, [readOnly, onNodesChange, setNodes, updateCurrentFlow]);

  // Handle edge changes
  const handleEdgesChange = useCallback((changes) => {
    if (readOnly) return;
    onEdgesChange(changes);

    // Immediate update for edge changes (edges are not dragged as frequently)
    const updatedEdges = applyEdgeChanges(changes, edges);
    updateCurrentFlow({ edges: updatedEdges });
  }, [readOnly, onEdgesChange, edges, updateCurrentFlow]);

  // Handle node drag stop - ensure final position is saved
  const onNodeDragStop = useCallback((event, node) => {
    if (readOnly) return;
    // Get the current nodes from ReactFlow internal state and save
    setNodes((currentNodes) => {
      updateCurrentFlow({ nodes: currentNodes, edges });
      return currentNodes;
    });
  }, [readOnly, edges, setNodes, updateCurrentFlow]);

  // Handle node selection
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  // Handle edge selection
  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  // Add new node
  const addNode = useCallback((type, position, nodeConfig) => {
    const finalPosition = position || project({
      x: window.innerWidth / 2 - 100,
      y: window.innerHeight / 2 - 50
    });

    const newNode = {
      id: `node-${Date.now()}`,
      type,
      position: finalPosition,
      data: {
        label: nodeConfig?.label || type,
        description: nodeConfig?.description || '',
        color: nodeConfig?.color || getNodeThemeColor(type)
      }
    };

    setNodes((nds) => [...nds, newNode]);
    updateCurrentFlow({ nodes: [...nodes, newNode] });
    enqueueSnackbar('Node added', { variant: 'success' });
  }, [project, nodes, setNodes, updateCurrentFlow, enqueueSnackbar]);

  // Duplicate node
  const duplicateNode = useCallback((node) => {
    if (readOnly) return;

    const newNode = {
      ...node,
      id: `node-${Date.now()}`,
      position: {
        x: node.position.x + 50,
        y: node.position.y + 50
      }
    };

    setNodes((nds) => [...nds, newNode]);
    updateCurrentFlow({ nodes: [...nodes, newNode] });
    enqueueSnackbar('Node duplicated', { variant: 'success' });
  }, [readOnly, nodes, setNodes, updateCurrentFlow, enqueueSnackbar]);

  // Delete selected elements
  const deleteSelected = useCallback(() => {
    if (readOnly) return;

    if (selectedNode) {
      setNodes((nds) => nds.filter(n => n.id !== selectedNode.id));
      setEdges((eds) => eds.filter(e =>
        e.source !== selectedNode.id && e.target !== selectedNode.id
      ));
      updateCurrentFlow({
        nodes: nodes.filter(n => n.id !== selectedNode.id),
        edges: edges.filter(e =>
          e.source !== selectedNode.id && e.target !== selectedNode.id
        )
      });
      setSelectedNode(null);
      enqueueSnackbar('Node deleted', { variant: 'info' });
    }

    if (selectedEdge) {
      setEdges((eds) => eds.filter(e => e.id !== selectedEdge.id));
      updateCurrentFlow({
        edges: edges.filter(e => e.id !== selectedEdge.id)
      });
      setSelectedEdge(null);
      enqueueSnackbar('Edge deleted', { variant: 'info' });
    }
  }, [readOnly, selectedNode, selectedEdge, nodes, edges, setNodes, setEdges, updateCurrentFlow, enqueueSnackbar]);

  // Close context menu
  const handleCloseContextMenu = useCallback(() => {
    setNodeMenuAnchor(null);
    setContextMenuNode(null);
  }, []);

  // Handle drag over
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop
  const onDrop = useCallback((event) => {
    event.preventDefault();

    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const type = event.dataTransfer.getData('application/reactflow');

    // Check if the dropped element is a valid node type
    if (typeof type === 'undefined' || !type) {
      return;
    }

    const position = project({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top,
    });

    // Find node configuration - using theme colors
    const nodeCategories = [
      {
        nodes: [
          { type: 'start', label: 'Start', description: 'Process start point', color: getNodeThemeColor('start') },
          { type: 'end', label: 'End', description: 'Process end point', color: getNodeThemeColor('end') }
        ]
      },
      {
        nodes: [
          { type: 'decision', label: 'Decision', description: 'Conditional branching', color: getNodeThemeColor('decision') },
          { type: 'parallelGateway', label: 'Parallel Gateway', description: 'Split into parallel paths', color: getNodeThemeColor('parallelGateway') },
          { type: 'mergeGateway', label: 'Merge Gateway', description: 'Merge parallel paths', color: getNodeThemeColor('mergeGateway') },
          { type: 'loop', label: 'Loop', description: 'Iterative processing', color: getNodeThemeColor('loop') }
        ]
      },
      {
        nodes: [
          { type: 'api', label: 'API Call', description: 'External API integration', color: getNodeThemeColor('api') },
          { type: 'database', label: 'Database', description: 'Database operations', color: getNodeThemeColor('database') },
          { type: 'email', label: 'Email', description: 'Send email notifications', color: getNodeThemeColor('email') },
          { type: 'approval', label: 'Approval', description: 'Human approval step', color: getNodeThemeColor('approval') }
        ]
      },
      {
        nodes: [
          { type: 'timer', label: 'Timer', description: 'Time-based delays', color: getNodeThemeColor('timer') },
          { type: 'errorHandler', label: 'Error Handler', description: 'Error handling logic', color: getNodeThemeColor('errorHandler') },
          { type: 'subprocess', label: 'Subprocess', description: 'Call another process', color: getNodeThemeColor('subprocess') }
        ]
      },
      {
        nodes: [
          { type: 'note', label: 'Note', description: 'Add documentation', color: getNodeThemeColor('note') }
        ]
      }
    ];

    const nodeConfig = nodeCategories
      .flatMap(cat => cat.nodes)
      .find(node => node.type === type);

    addNode(type, position, nodeConfig);
  }, [project, addNode, getNodeThemeColor]);

  // Save flow
  const handleSave = useCallback(async () => {
    if (readOnly) return;

    try {
      const viewport = getViewport();
      const flowData = {
        nodes,
        edges,
        viewport
      };

      if (flowId) {
        // Update existing flow
        await updateProcessFlow(flowId, flowData);
        enqueueSnackbar('Process flow saved successfully', { variant: 'success' });
      } else {
        // Create new flow
        const newFlow = await createProcessFlow({
          name: 'New Process Flow',
          description: 'Created from editor',
          category: 'process',
          status: 'draft',
          ...flowData
        });
        enqueueSnackbar('Process flow created successfully', { variant: 'success' });
        // Navigate to edit mode with the new flow ID
        navigate(`/process-flows/${newFlow._id}/edit`);
      }
    } catch (error) {
      const action = flowId ? 'save' : 'create';
      enqueueSnackbar(`Failed to ${action} process flow`, { variant: 'error' });
    }
  }, [flowId, readOnly, nodes, edges, getViewport, updateProcessFlow, createProcessFlow, navigate, enqueueSnackbar]);

  // Update node data
  const updateNodeData = useCallback((nodeId, data) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
      )
    );
    const updatedNodes = nodes.map((node) =>
      node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
    );
    updateCurrentFlow({ nodes: updatedNodes });
  }, [nodes, setNodes, updateCurrentFlow]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      reactFlowWrapper.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Export flow
  const handleExport = async (format = 'png') => {
    try {
      const fileName = `${currentFlow?.name || 'process-flow'}_${new Date().toISOString().split('T')[0]}`;

      if (format === 'json') {
        // Export as JSON for data backup
        const exportData = {
          name: currentFlow?.name,
          description: currentFlow?.description,
          category: currentFlow?.category,
          tags: currentFlow?.tags,
          nodes: nodes,
          edges: edges,
          viewport: currentFlow?.viewport,
          settings: currentFlow?.settings,
          exportedAt: new Date(),
        };

        const dataStr = JSON.stringify(exportData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        enqueueSnackbar('Process flow data exported as JSON', { variant: 'success' });
      } else {
        // Export as image (PNG or SVG)
        if (!nodes || nodes.length === 0) {
          throw new Error('No nodes to export');
        }

        // Get all node bounds for calculating the canvas
        const nodesBounds = getRectOfNodes(nodes);

        // Account for actual node dimensions (nodes extend beyond their center position)
        // Typical node width is ~150-200px, height is ~100-150px
        const nodeExtension = 100; // Half of typical node width/height

        // Add generous padding all around
        const padding = 100;

        // Calculate the full extent including node dimensions
        const fullBounds = {
          x: nodesBounds.x - nodeExtension,
          y: nodesBounds.y - nodeExtension,
          width: nodesBounds.width + (nodeExtension * 2),
          height: nodesBounds.height + (nodeExtension * 2)
        };

        // Final image dimensions
        const imageWidth = fullBounds.width + (padding * 2);
        const imageHeight = fullBounds.height + (padding * 2);

        console.log('Export dimensions:', {
          nodes: nodes.length,
          bounds: fullBounds,
          imageSize: { width: imageWidth, height: imageHeight }
        });

        // Get viewport for capturing
        const viewportElement = document.querySelector('.react-flow__viewport');
        if (!viewportElement) {
          throw new Error('Flow viewport not found');
        }

        // Calculate transform to position all content correctly
        // Move the content so the top-left of our bounds is at the padding position
        const translateX = -fullBounds.x + padding;
        const translateY = -fullBounds.y + padding;

        let dataUrl;

        const exportOptions = {
          backgroundColor: theme.palette.background.default,
          width: imageWidth,
          height: imageHeight,
          filter: (node) => {
            // Exclude UI elements from export
            const className = node?.className || '';
            if (typeof className === 'string') {
              if (className.includes('react-flow__minimap')) return false;
              if (className.includes('react-flow__controls')) return false;
              if (className.includes('react-flow__attribution')) return false;
            }
            return true;
          },
          style: {
            transform: `translate(${translateX}px, ${translateY}px)`,
            transformOrigin: '0 0',
          }
        };

        try {
          if (format === 'svg') {
            dataUrl = await toSvg(viewportElement, exportOptions);
          } else {
            dataUrl = await toPng(viewportElement, {
              ...exportOptions,
              pixelRatio: 2, // Higher quality for PNG
              cacheBust: true,
            });
          }
        } catch (err) {
          console.error('Export failed:', err);
          throw new Error('Failed to generate image. Please try zooming out to see all nodes and try again.');
        }

        // Create download link
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${fileName}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        enqueueSnackbar(`Process flow exported as ${format.toUpperCase()}`, { variant: 'success' });
      }
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Failed to export process flow', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <Typography variant="h6" sx={{ flexGrow: 0, mr: 2 }}>
            {flowId ? (currentFlow?.name || 'Process Flow Editor') : 'Create New Process Flow'}
          </Typography>

          {currentFlow?.status && (
            <Chip
              label={currentFlow.status}
              size="small"
              color={currentFlow.status === 'published' ? 'success' : 'default'}
              sx={{ mr: 2 }}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Auto-save indicator */}
          {isAutoSaving && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Auto-saving...</Typography>
            </Stack>
          )}

          {lastAutoSaveAt && !isAutoSaving && (
            <Typography variant="caption" sx={{ mr: 2 }}>
              Last saved: {new Date(lastAutoSaveAt).toLocaleTimeString()}
            </Typography>
          )}

          {/* Action buttons */}
          <ButtonGroup size="small" variant="contained">
            <Tooltip title={flowId ? "Save" : "Create Flow"}>
              <Button
                onClick={handleSave}
                disabled={readOnly || (!flowId && nodes.length === 0)}
                startIcon={<SaveIcon />}
              >
                {flowId ? 'Save' : 'Create Flow'}
              </Button>
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

          <IconButton onClick={toggleFullscreen} size="small">
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>

          <IconButton onClick={() => setShowShareDialog(true)} size="small">
            <ShareIcon />
          </IconButton>

          <IconButton
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            size="small"
            aria-label="Export process flow"
          >
            <DownloadIcon />
          </IconButton>

          <IconButton onClick={() => setShowVersionHistory(true)} size="small">
            <HistoryIcon />
          </IconButton>

          <IconButton onClick={() => setShowComments(!showComments)} size="small">
            <CommentIcon />
          </IconButton>

          <IconButton onClick={() => setShowSettings(true)} size="small">
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main editor area */}
      <Box sx={{ flexGrow: 1, position: 'relative' }} ref={reactFlowWrapper}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onNodeDragStop={onNodeDragStop}
            nodeTypes={nodeTypes}
            fitView
            attributionPosition="bottom-left"
            deleteKeyCode={readOnly ? null : 'Delete'}
            multiSelectionKeyCode={readOnly ? null : 'Shift'}
            panOnDrag={!readOnly}
            zoomOnScroll={!readOnly}
            preventScrolling={true}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            elementsSelectable={!readOnly}
            onDrop={onDrop}
            onDragOver={onDragOver}
            style={reactFlowStyles}
            proOptions={{ hideAttribution: true }}
          >
            <Controls />
            <MiniMap />
            <Background
              variant="dots"
              gap={16}
              size={1}
              color={theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]}
            />

            {/* Node palette */}
            {!readOnly && (
              <NodePalette
                onAddNode={addNode}
                disabled={readOnly}
              />
            )}

            {/* Selected element actions */}
            {(selectedNode || selectedEdge) && !readOnly && (
              <Panel position="bottom-center">
                <Paper sx={{ p: 1 }}>
                  <Stack direction="row" spacing={1}>
                    {selectedNode && (
                      <Button
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => setShowNodeEditor(true)}
                      >
                        Edit Node
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={deleteSelected}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Paper>
              </Panel>
            )}
          </ReactFlow>
        )}

      </Box>

      {/* Dialogs and panels */}
      {selectedNode && showNodeEditor && (
        <NodeEditor
          node={selectedNode}
          onUpdate={updateNodeData}
          onClose={() => setShowNodeEditor(false)}
        />
      )}

      {showShareDialog && (
        <ShareDialog
          flowId={flowId}
          open={showShareDialog}
          onClose={() => setShowShareDialog(false)}
        />
      )}

      {showComments && (
        <CommentPanel
          flowId={flowId}
          selectedNodeId={selectedNode?.id}
          onClose={() => setShowComments(false)}
        />
      )}

      {showVersionHistory && (
        <VersionHistory
          flowId={flowId}
          open={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
        />
      )}

      {showSettings && (
        <FlowSettings
          flow={currentFlow}
          open={showSettings}
          onClose={() => setShowSettings(false)}
          isCreateMode={!flowId}
        />
      )}

      {/* Context Menu */}
      {nodeMenuAnchor && contextMenuNode && (
        <Menu
          open={Boolean(nodeMenuAnchor)}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            nodeMenuAnchor ? { top: nodeMenuAnchor.mouseY, left: nodeMenuAnchor.mouseX } : undefined
          }
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            onClick={() => {
              setShowNodeEditor(true);
              handleCloseContextMenu();
            }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Node</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              duplicateNode(contextMenuNode);
              handleCloseContextMenu();
            }}
          >
            <ListItemIcon>
              <DuplicateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              deleteSelected();
              handleCloseContextMenu();
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      )}

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            handleExport('png');
            setExportMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export as PNG" secondary="High-quality image" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleExport('svg');
            setExportMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export as SVG" secondary="Scalable vector graphic" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleExport('json');
            setExportMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <CodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export as JSON" secondary="Data backup" />
        </MenuItem>
      </Menu>
    </Box>
  );
}

// Wrap with ReactFlowProvider
export default function ProcessFlowEditorWrapper(props) {
  return (
    <ReactFlowProvider>
      <ProcessFlowEditor {...props} />
    </ReactFlowProvider>
  );
}